/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1943732227 — Логування подій (event_log) */

import { useEffect, useRef } from 'react';

import { IS_PROD_DB } from '../../../shared/env.config';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useTimerStore } from '../../../shared/hooks/useTimerStore';
import { debug } from '../../../shared/lib/general';
import { EVENT_LOG_ENDPOINTS } from '../api/event-log.api-constants';
import { CreateEventLogRequestDto } from '../api/event-log.api-types';
import { screenFromPath } from '../screenFromPath';

/**
 * Emits a `page hidden - {screen}` event via `navigator.sendBeacon` when the
 * tab is backgrounded or closed (SWRN-1639, A2 + A3). Mounted once in the app
 * shell so a single listener covers every screen.
 *
 * Listens on `visibilitychange` (hidden) and `pagehide` — deliberately NOT
 * `beforeunload`, which does not fire reliably on mobile Safari. `sendBeacon`
 * is inherently keepalive, so the request survives the page going away.
 */
export const useFlushOnHide = (): void => {
    // Guards against the double-fire of visibilitychange + pagehide for a
    // single hide. Reset when the page becomes visible again (pageshow).
    const hasFlushedRef = useRef(false);

    useEffect(() => {
        const flush = (): void => {
            if (hasFlushedRef.current || !IS_PROD_DB) {
                return;
            }

            const orderId = Number(useAppStore.getState().order?.id);
            if (!Number.isInteger(orderId) || orderId <= 0) {
                return;
            }

            const eventName = screenFromPath(window.location.pathname);
            if (!eventName) {
                return;
            }

            const dto: CreateEventLogRequestDto = {
                order_id: orderId,
                type: 'event',
                page_url: window.location.pathname,
                timeout_name: eventName,
                timeout_value: useTimerStore.getState().time,
            };

            // application/json Blob: the endpoint is same-origin so no CORS
            // preflight is triggered, and the existing backend parses it as-is.
            const blob = new Blob([JSON.stringify(dto)], {
                type: 'application/json',
            });
            const queued = navigator.sendBeacon(EVENT_LOG_ENDPOINTS.CREATE, blob);
            hasFlushedRef.current = true;

            if (!queued) {
                debug('event-log page-hidden beacon rejected', 'warn');
            }
        };

        const onVisibilityChange = (): void => {
            if (document.visibilityState === 'hidden') {
                flush();
            } else {
                hasFlushedRef.current = false;
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', flush);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', flush);
        };
    }, []);
};
